/**
 * Step 1 — Upload Work Order Data.
 *
 * Drag-drop one or more Element exports (each `output.mdb` or a `.zip` of `Out*.csv` is its OWN work
 * order — the flow supports MULTIPLE work orders at once). Each upload reuses the existing
 * `POST /api/sources/upload`; the response surfaces a "Found N samples, M analytes…" summary. A built-in
 * synthetic sample is always available so the wizard is usable with zero infrastructure.
 */
import { useUploadSource } from "../../api/sources";
import { errorMessage } from "../../api/client";
import type { SourceKind, SourceUpload } from "../../api/types";
import { Dropzone } from "../../components/Dropzone";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { useToast } from "../../components/Toast";
import { track, EVENTS } from "./telemetry";
import { SYNTHETIC_WORK_ORDER, type WorkOrder } from "./types";

function guessKind(file: File): SourceKind {
  return file.name.toLowerCase().endsWith(".zip") ? "csv_zip" : "output_mdb";
}

function workOrderFromUpload(up: SourceUpload): WorkOrder {
  return {
    id: up.id,
    source: up.kind === "csv_zip" ? { csv_zip_id: up.id } : { output_mdb_id: up.id },
    label: up.work_order?.trim() || up.filename,
    kind: up.kind === "csv_zip" ? "csv_zip" : "output_mdb",
    samples: up.row_estimate ?? null,
    workOrderNumber: up.work_order ?? null,
    warnings: up.warnings ?? [],
  };
}

export function UploadStep({
  workOrders,
  onChange,
}: {
  workOrders: WorkOrder[];
  onChange: (next: WorkOrder[]) => void;
}) {
  const toast = useToast();
  const uploadSource = useUploadSource();

  const uploaded = workOrders.filter((w) => w.kind !== "synthetic");
  const usingSynthetic = workOrders.some((w) => w.kind === "synthetic");

  const handleUpload = async (file: File | undefined) => {
    if (!file) return;
    const kind = guessKind(file);
    try {
      const up = await uploadSource.mutateAsync({ file, kind });
      track(EVENTS.file_uploaded, { file_type: kind, size: file.size });
      const wo = workOrderFromUpload(up);
      // replace synthetic with real work orders once the user uploads something
      const next = workOrders.filter((w) => w.kind !== "synthetic" && w.id !== wo.id);
      onChange([...next, wo]);
      toast.success(`Added work order: ${wo.label}`);
    } catch (err) {
      track(EVENTS.error_encountered, { page: "generate_flow_upload", error_type: "upload_failed" });
      toast.error(errorMessage(err));
    }
  };

  const remove = (id: string) => {
    const next = workOrders.filter((w) => w.id !== id);
    onChange(next.length ? next : [SYNTHETIC_WORK_ORDER]);
  };

  const addSynthetic = () => {
    if (!usingSynthetic) onChange([...workOrders, SYNTHETIC_WORK_ORDER]);
  };

  return (
    <div className="stack">
      <div>
        <h2 style={{ marginBottom: 4 }}>Upload your work order data</h2>
        <p className="muted" style={{ margin: 0 }}>
          Drop one Element export per work order — an <code>output.mdb</code> or a <code>.zip</code> of
          your <code>Out*.csv</code> files. You can add as many work orders as you need.
        </p>
      </div>

      <Dropzone
        label="Upload your output.mdb or Out*.csv files"
        sublabel="Each file becomes its own work order. Drag in several, one at a time."
        hint="A .zip of Out*.csv files also works."
        accept=".mdb,.zip,application/x-msaccess,application/zip"
        loading={uploadSource.isPending}
        loadingLabel="Reading your work order…"
        onFile={(file) => void handleUpload(file)}
      />

      {uploaded.length > 0 && (
        <div className="stack" style={{ gap: "var(--space-2)" }}>
          <h3 style={{ margin: 0 }}>Work orders ({uploaded.length})</h3>
          {uploaded.map((wo) => (
            <div key={wo.id} className="card card--pad-sm row row--between row--wrap">
              <div className="stack" style={{ gap: 2 }}>
                <div className="row" style={{ gap: "var(--space-2)", alignItems: "center" }}>
                  <strong>{wo.label}</strong>
                  <Badge tone="accent">{wo.kind === "csv_zip" ? "CSV zip" : "output.mdb"}</Badge>
                </div>
                <span className="faint">{summaryLine(wo)}</span>
                {(wo.warnings ?? []).slice(0, 1).map((w, i) => (
                  <span key={i} className="text-warning faint">
                    {w}
                  </span>
                ))}
              </div>
              <Button variant="ghost" size="sm" onClick={() => remove(wo.id)}>
                Remove
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="card card--pad-sm row row--between row--wrap">
        <div className="stack" style={{ gap: 2 }}>
          <strong>No data handy?</strong>
          <span className="faint">
            Use the built-in sample work order to try the full flow with realistic synthetic data.
          </span>
        </div>
        {usingSynthetic ? (
          <Badge tone="success">Sample data included</Badge>
        ) : (
          <Button variant="ghost" size="sm" onClick={addSynthetic}>
            Add sample data
          </Button>
        )}
      </div>
    </div>
  );
}

function summaryLine(wo: WorkOrder): string {
  if (wo.kind === "synthetic") return "Built-in synthetic samples.";
  const parts: string[] = [];
  if (wo.samples != null && wo.samples > 0) parts.push(`${wo.samples} rows`);
  if (wo.workOrderNumber) parts.push(`Work Order #${wo.workOrderNumber}`);
  return parts.length
    ? `Found ${parts.join(" · ")}.`
    : "Stored — we'll read the samples, analytes and matrices when you preview.";
}
